import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {defineMessages, injectIntl} from 'react-intl';
import intlShape from '../lib/intlShape.js';
import VM from '@scratch/scratch-vm';
import aiIcon from '../components/action-menu/icon--ai.svg';
import AssetPanel from '../components/asset-panel/asset-panel.jsx';
import PaintEditorWrapper from './paint-editor-wrapper.jsx';
import BackdropCreator from '../components/ai-backdrop-creator/backdropCreator.jsx';
import SpriteCreator from '../components/ai-sprite-creator/spriteCreator.jsx';
import {connect} from 'react-redux';
import {handleFileUpload, costumeUpload} from '../lib/file-uploader.js';
import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import DragConstants from '../lib/drag-constants';
import {emptyCostume} from '../lib/empty-assets';
import sharedMessages from '../lib/shared-messages';
import downloadBlob from '../lib/download-blob';

import {
    openCostumeLibrary,
    openBackdropLibrary
} from '../reducers/modals';

import {
    activateTab,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {setRestore} from '../reducers/restore-deletion';
import {showStandardAlert, closeAlertWithId} from '../reducers/alerts';

import addLibraryBackdropIcon from '../components/asset-panel/icon--add-backdrop-lib.svg';
import addLibraryCostumeIcon from '../components/asset-panel/icon--add-costume-lib.svg';
import fileUploadIcon from '../components/action-menu/icon--file-upload.svg';
import paintIcon from '../components/action-menu/icon--paint.svg';
import surpriseIcon from '../components/action-menu/icon--surprise.svg';
// import searchIcon from '../components/action-menu/icon--search.svg';

import costumeLibraryContent from '../lib/libraries/costumes.json';
import backdropLibraryContent from '../lib/libraries/backdrops.json';
import {ModalFocusContext} from '../contexts/modal-focus-context.jsx';
import {costumeShape} from '../lib/assets-prop-types.js';
import mergeDynamicAssets from '../lib/merge-dynamic-assets.js';

let messages = defineMessages({
    addLibraryBackdropMsg: {
        defaultMessage: 'Choose a Backdrop',
        description: 'Button to add a backdrop in the editor tab',
        id: 'gui.costumeTab.addBackdropFromLibrary'
    },
    addLibraryCostumeMsg: {
        defaultMessage: 'Choose a Costume',
        description: 'Button to add a costume in the editor tab',
        id: 'gui.costumeTab.addCostumeFromLibrary'
    },
    addBlankCostumeMsg: {
        defaultMessage: 'Paint',
        description: 'Button to add a blank costume in the editor tab',
        id: 'gui.costumeTab.addBlankCostume'
    },
    addSurpriseCostumeMsg: {
        defaultMessage: 'Surprise',
        description: 'Button to add a surprise costume in the editor tab',
        id: 'gui.costumeTab.addSurpriseCostume'
    },
    addFileBackdropMsg: {
        defaultMessage: 'Upload Backdrop',
        description: 'Button to add a backdrop by uploading a file in the editor tab',
        id: 'gui.costumeTab.addFileBackdrop'
    },
    addFileCostumeMsg: {
        defaultMessage: 'Upload Costume',
        description: 'Button to add a costume by uploading a file in the editor tab',
        id: 'gui.costumeTab.addFileCostume'
    }
});

messages = {...messages, ...sharedMessages};

class CostumeTab extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSelectCostume',
            'handleDeleteCostume',
            'handleDuplicateCostume',
            'handleExportCostume',
            'handleNewCostume',
            'handleNewBackdropClick',
            'handleNewBlankCostume',
            'handleNewCostumeClick',
            'handleSurpriseCostume',
            'handleSurpriseBackdrop',
            'handleFileUploadClick',
            'handleCostumeUpload',
            'handleDrop',
            'setFileInput',
            'mergeDynamicCostumes',
            'mergeDynamicBackdrops',
            'handleAIBackdropClick',
            'handleAIBackdropAdd',
            'handleAICostumeClick',
            'handleAICostumeAdd'
        ]);
        const {
            editingTarget,
            sprites,
            stage
        } = props;
        const target = editingTarget && sprites[editingTarget] ? sprites[editingTarget] : stage;
        if (target && target.currentCostume) {
            this.state = {selectedCostumeIndex: target.currentCostume};
        } else {
            this.state = {selectedCostumeIndex: 0};
        }
        this.processedCostumes = {};
        this.processedBackdrops = {};
        this.state.showAIBackdropCreator = false;
        this.state.showAICostumeCreator = false;
    }
    componentDidMount () {
        this.handleDocumentClick = () => {
            // If the costume tab is focused and the user clicks outside of it, unfocus the costume tab.
            // This is to prevent keypresses from affecting the tabs when users try to interact with the paint editor
            if (document.activeElement instanceof HTMLLIElement && document.activeElement.role === 'tab') {
                document.activeElement.blur();
            }
        };
        document.addEventListener('mousedown', this.handleDocumentClick);
    }

    componentWillReceiveProps (nextProps) {
        const {
            editingTarget,
            sprites,
            stage
        } = nextProps;

        const target = editingTarget && sprites[editingTarget] ? sprites[editingTarget] : stage;
        if (!target || !target.costumes) {
            return;
        }

        if (this.props.editingTarget === editingTarget) {
            // If costumes have been added or removed, change costumes to the editing target's
            // current costume.
            const oldTarget = this.props.sprites[editingTarget] ?
                this.props.sprites[editingTarget] : this.props.stage;
            // @todo: Find and switch to the index of the costume that is new. This is blocked by
            // https://github.com/LLK/scratch-vm/issues/967
            // Right now, you can land on the wrong costume if a costume changing script is running.
            if (oldTarget.costumeCount !== target.costumeCount) {
                this.setState({selectedCostumeIndex: target.currentCostume});
            }
        } else {
            // If switching editing targets, update the costume index
            this.setState({selectedCostumeIndex: target.currentCostume});
        }
    }

    componentWillUnmount () {
        document.removeEventListener('mousedown', this.handleDocumentClick);
    }
    static contextType = ModalFocusContext;

    mergeDynamicCostumes () {
        if (this.processedCostumes.source === this.props.dynamicCostumes) {
            return this.processedCostumes.data;
        }
        this.processedCostumes = mergeDynamicAssets(
            costumeLibraryContent,
            this.props.dynamicCostumes
        );
        return this.processedCostumes.data;
    }
    mergeDynamicBackdrops () {
        if (this.processedBackdrops.source === this.props.dynamicBackdrops) {
            return this.processedBackdrops.data;
        }
        this.processedBackdrops = mergeDynamicAssets(
            backdropLibraryContent,
            this.props.dynamicBackdrops
        );
        return this.processedBackdrops.data;
    }
    handleSelectCostume (costumeIndex) {
        this.props.vm.editingTarget.setCostume(costumeIndex);
        this.setState({selectedCostumeIndex: costumeIndex});
    }
    handleDeleteCostume (costumeIndex) {
        const restoreCostumeFun = this.props.vm.deleteCostume(costumeIndex);
        this.props.dispatchUpdateRestore({
            restoreFun: restoreCostumeFun,
            deletedItem: 'Costume'
        });
    }
    handleDuplicateCostume (costumeIndex) {
        this.props.vm.duplicateCostume(costumeIndex);
    }
    handleExportCostume (costumeIndex) {
        const item = this.props.vm.editingTarget.sprite.costumes[costumeIndex];
        const blob = new Blob([item.asset.data], {type: item.asset.assetType.contentType});
        downloadBlob(`${item.name}.${item.asset.dataFormat}`, blob);
    }
    handleNewCostume (costume, fromCostumeLibrary, targetId) {
        const costumes = Array.isArray(costume) ? costume : [costume];

        return Promise.all(costumes.map(c => {
            if (fromCostumeLibrary) {
                return this.props.vm.addCostumeFromLibrary(c.md5, c);
            }
            // If targetId is falsy, VM should default it to editingTarget.id
            // However, targetId should be provided to prevent #5876,
            // if making new costume takes a while
            return this.props.vm.addCostume(c.md5, c, targetId);
        }));
    }
    handleNewBackdropClick (e) {
        e.preventDefault();
        this.context.captureFocus();
        this.props.onNewLibraryBackdropClick(jsonStr => {
            const costume = JSON.parse(jsonStr);
            this.handleNewCostume(costume, true);
        });
    }
    handleNewCostumeClick (e) {
        e.preventDefault();
        this.context.captureFocus();
        this.props.onNewLibraryCostumeClick(jsonStr => {
            const costume = JSON.parse(jsonStr);
            this.handleNewCostume(costume, true);
        });
    }
    handleNewBlankCostume () {
        const name = this.props.vm.editingTarget.isStage ?
            this.props.intl.formatMessage(messages.backdrop, {index: 1}) :
            this.props.intl.formatMessage(messages.costume, {index: 1});
        this.handleNewCostume(emptyCostume(name));
    }
    handleSurpriseCostume () {
        const costumes = this.mergeDynamicCostumes();

        const item = costumes[Math.floor(Math.random() * costumes.length)];
        const vmCostume = {
            name: item.name,
            md5: item.md5ext,
            rotationCenterX: item.rotationCenterX,
            rotationCenterY: item.rotationCenterY,
            bitmapResolution: item.bitmapResolution,
            skinId: null
        };
        this.handleNewCostume(vmCostume, true /* fromCostumeLibrary */);
    }
    handleSurpriseBackdrop () {
        const backdrops = this.mergeDynamicBackdrops();

        const item = backdrops[Math.floor(Math.random() * backdrops.length)];
        const vmCostume = {
            name: item.name,
            md5: item.md5ext,
            rotationCenterX: item.rotationCenterX,
            rotationCenterY: item.rotationCenterY,
            bitmapResolution: item.bitmapResolution,
            skinId: null
        };
        this.handleNewCostume(vmCostume);
    }

    handleAIBackdropClick () {
        this.setState({showAIBackdropCreator: true});
    }

    handleAIBackdropAdd (imageBase64) {
        const storage = this.props.vm.runtime.storage;
        const targetId = this.props.vm.editingTarget.id;

        this.props.onShowImporting();

        try {
            const cleanBase64 = imageBase64.replace(
                /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                ''
            );

            const image = new Image();

            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 480;
                canvas.height = 360;

                const context = canvas.getContext('2d');

                const sourceAspect = image.width / image.height;
                const targetAspect = 480 / 360;

                let sourceX = 0;
                let sourceY = 0;
                let sourceWidth = image.width;
                let sourceHeight = image.height;

                if (sourceAspect > targetAspect) {
                    sourceWidth = image.height * targetAspect;
                    sourceX = (image.width - sourceWidth) / 2;
                } else {
                    sourceHeight = image.width / targetAspect;
                    sourceY = (image.height - sourceHeight) / 2;
                }

                context.drawImage(
                    image,
                    sourceX,
                    sourceY,
                    sourceWidth,
                    sourceHeight,
                    0,
                    0,
                    480,
                    360
                );

                canvas.toBlob(blob => {
                    if (!blob) {
                        console.error('[AI BACKDROP] Failed to create PNG blob.');
                        this.props.onCloseImporting();
                        return;
                    }

                    blob.arrayBuffer()
                        .then(buffer => {
                            costumeUpload(
                                buffer,
                                'image/png',
                                storage,
                                vmCostumes => {
                                    vmCostumes.forEach(costume => {
                                        costume.name = 'AI Backdrop';
                                    });

                                    this.handleNewCostume(
                                        vmCostumes,
                                        false,
                                        targetId
                                    ).then(() => {
                                        this.props.onCloseImporting();
                                    }).catch(error => {
                                        console.error(
                                            '[AI BACKDROP] Failed to add backdrop:',
                                            error
                                        );
                                        this.props.onCloseImporting();
                                    });
                                },
                                this.props.onCloseImporting
                            );
                        })
                        .catch(error => {
                            console.error(
                                '[AI BACKDROP] Failed to read PNG blob:',
                                error
                            );
                            this.props.onCloseImporting();
                        });
                }, 'image/png');
            };

            image.onerror = error => {
                console.error(
                    '[AI BACKDROP] Failed to load generated image:',
                    error
                );
                this.props.onCloseImporting();
            };

            image.src = `data:image/png;base64,${cleanBase64}`;
        } catch (error) {
            console.error(
                '[AI BACKDROP] decode/import failed:',
                error
            );
            this.props.onCloseImporting();
        }
    }

    handleAICostumeAdd (imageBase64) {
        const storage = this.props.vm.runtime.storage;
        const targetId = this.props.vm.editingTarget.id;

        this.props.onShowImporting();

        try {
            const cleanBase64 = imageBase64.replace(
                /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                ''
            );

            const image = new Image();

            image.onload = () => {
                const sourceCanvas = document.createElement('canvas');
                sourceCanvas.width = image.width;
                sourceCanvas.height = image.height;

                const sourceContext = sourceCanvas.getContext('2d');
                sourceContext.drawImage(image, 0, 0);

                const imageData = sourceContext.getImageData(
                    0,
                    0,
                    image.width,
                    image.height
                );

                const pixels = imageData.data;

                let minX = image.width;
                let minY = image.height;
                let maxX = -1;
                let maxY = -1;

                for (let y = 0; y < image.height; y++) {
                    for (let x = 0; x < image.width; x++) {
                        const alpha = pixels[((y * image.width) + x) * 4 + 3];

                        if (alpha > 10) {
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }

                if (maxX < minX || maxY < minY) {
                    console.error('[AI COSTUME] Generated image is empty.');
                    this.props.onCloseImporting();
                    return;
                }

                const sourceWidth = maxX - minX + 1;
                const sourceHeight = maxY - minY + 1;

                const maxCostumeDimension = 220;
                const padding = 12;

                const scale = Math.min(
                    1,
                    maxCostumeDimension / Math.max(sourceWidth, sourceHeight)
                );

                const outputWidth = Math.ceil(sourceWidth * scale);
                const outputHeight = Math.ceil(sourceHeight * scale);

                const canvas = document.createElement('canvas');
                canvas.width = outputWidth + (padding * 2);
                canvas.height = outputHeight + (padding * 2);

                const context = canvas.getContext('2d');

                context.drawImage(
                    image,
                    minX,
                    minY,
                    sourceWidth,
                    sourceHeight,
                    padding,
                    padding,
                    outputWidth,
                    outputHeight
                );

                canvas.toBlob(blob => {
                    if (!blob) {
                        console.error('[AI COSTUME] Failed to create PNG blob.');
                        this.props.onCloseImporting();
                        return;
                    }

                    blob.arrayBuffer()
                        .then(buffer => {
                            costumeUpload(
                                buffer,
                                'image/png',
                                storage,
                                vmCostumes => {
                                    vmCostumes.forEach(costume => {
                                        costume.name = 'AI Costume';
                                    });

                                    this.handleNewCostume(
                                        vmCostumes,
                                        false,
                                        targetId
                                    ).then(() => {
                                        this.props.onCloseImporting();
                                    }).catch(error => {
                                        console.error(
                                            '[AI COSTUME] Failed to add costume:',
                                            error
                                        );
                                        this.props.onCloseImporting();
                                    });
                                },
                                this.props.onCloseImporting
                            );
                        })
                        .catch(error => {
                            console.error(
                                '[AI COSTUME] Failed to read PNG blob:',
                                error
                            );
                            this.props.onCloseImporting();
                        });
                }, 'image/png');
            };

            image.onerror = error => {
                console.error(
                    '[AI COSTUME] Failed to load generated image:',
                    error
                );
                this.props.onCloseImporting();
            };

            image.src = `data:image/png;base64,${cleanBase64}`;
        } catch (error) {
            console.error(
                '[AI COSTUME] decode/import failed:',
                error
            );
            this.props.onCloseImporting();
        }
    }

    handleAICostumeClick () {
        this.setState({showAICostumeCreator: true});
    }


    handleCostumeUpload (e) {
        const storage = this.props.vm.runtime.storage;
        const targetId = this.props.vm.editingTarget.id;
        this.props.onShowImporting();
        handleFileUpload(e.target, (buffer, fileType, fileName, fileIndex, fileCount) => {
            costumeUpload(buffer, fileType, storage, vmCostumes => {
                vmCostumes.forEach((costume, i) => {
                    costume.name = `${fileName}${i ? i + 1 : ''}`;
                });
                this.handleNewCostume(vmCostumes, false, targetId).then(() => {
                    if (fileIndex === fileCount - 1) {
                        this.props.onCloseImporting();
                    }
                });
            }, this.props.onCloseImporting);
        }, this.props.onCloseImporting);
    }
    handleFileUploadClick () {
        this.fileInput.click();
    }
    handleDrop (dropInfo) {
        if (dropInfo.dragType === DragConstants.COSTUME) {
            const sprite = this.props.vm.editingTarget.sprite;
            const activeCostume = sprite.costumes[this.state.selectedCostumeIndex];
            this.props.vm.reorderCostume(this.props.vm.editingTarget.id,
                dropInfo.index, dropInfo.newIndex);
            this.setState({selectedCostumeIndex: sprite.costumes.indexOf(activeCostume)});
        } else if (dropInfo.dragType === DragConstants.BACKPACK_COSTUME) {
            this.props.vm.addCostume(dropInfo.payload.body, {
                name: dropInfo.payload.name
            });
        } else if (dropInfo.dragType === DragConstants.BACKPACK_SOUND) {
            this.props.onActivateSoundsTab();
            this.props.vm.addSound({
                md5: dropInfo.payload.body,
                name: dropInfo.payload.name
            });
        }
    }
    setFileInput (input) {
        this.fileInput = input;
    }
    formatCostumeDetails (size, optResolution) {
        // If no resolution is given, assume that the costume is an SVG
        const resolution = optResolution ? optResolution : 1;
        // Convert size to stage units by dividing by resolution
        // Round up width and height for scratch-flash compatibility
        // https://github.com/LLK/scratch-flash/blob/9fbac92ef3d09ceca0c0782f8a08deaa79e4df69/src/ui/media/MediaInfo.as#L224-L237
        return `${Math.ceil(size[0] / resolution)} x ${Math.ceil(size[1] / resolution)}`;
    }
    render () {
        const {
            ariaLabel,
            ariaRole,
            dispatchUpdateRestore,
            intl,
            isRtl,
            vm
        } = this.props;

        if (!vm.editingTarget) {
            return null;
        }

        const isStage = vm.editingTarget.isStage;
        const target = vm.editingTarget.sprite;

        const addLibraryMessage = isStage ? messages.addLibraryBackdropMsg : messages.addLibraryCostumeMsg;
        const addFileMessage = isStage ? messages.addFileBackdropMsg : messages.addFileCostumeMsg;
        const addSurpriseFunc = isStage ? this.handleSurpriseBackdrop : this.handleSurpriseCostume;
        const addLibraryFunc = isStage ? this.handleNewBackdropClick : this.handleNewCostumeClick;
        const addLibraryIcon = isStage ? addLibraryBackdropIcon : addLibraryCostumeIcon;
const assetButtons = [
    {
        title: isStage ? 'Backdrops' : 'Costumes',
        img: isStage ? addLibraryBackdropIcon : addLibraryCostumeIcon,
        onClick: () => {}
    },
    {
        title: isStage ? 'Create Backdrop with AI' : 'Create Costume with AI',
        img: aiIcon,
        onClick: isStage ? this.handleAIBackdropClick : this.handleAICostumeClick
    },
    {
        title: intl.formatMessage(addLibraryMessage),
        img: addLibraryIcon,
        onClick: addLibraryFunc
    },
    {
        title: intl.formatMessage(addFileMessage),
        img: fileUploadIcon,
        onClick: this.handleFileUploadClick,
        fileAccept: '.svg, .png, .bmp, .jpg, .jpeg, .gif',
        fileChange: this.handleCostumeUpload,
        fileInput: this.setFileInput,
        fileMultiple: true
    },
    {
        title: intl.formatMessage(messages.addSurpriseCostumeMsg),
        img: surpriseIcon,
        onClick: addSurpriseFunc
    },
    {
        title: intl.formatMessage(messages.addBlankCostumeMsg),
        img: paintIcon,
        onClick: this.handleNewBlankCostume
    }
];



// if (isStage) {
//     assetButtons.unshift({
//         title: 'Create Backdrop with AI',
//         img: aiIcon,
//         onClick: this.handleAIBackdropClick
//     });
// }
        
        const costumeData = target.costumes ? target.costumes.map(costume => ({
            name: costume.name,
            asset: costume.asset,
            details: costume.size ? this.formatCostumeDetails(costume.size, costume.bitmapResolution) : null,
            dragPayload: costume
        })) : [];
        return (
            <AssetPanel
                ariaLabel={ariaLabel}
                ariaRole={ariaRole}
              buttons={assetButtons}
                dragType={DragConstants.COSTUME}
                isRtl={isRtl}
                items={costumeData}
                selectedItemIndex={this.state.selectedCostumeIndex}
                onDeleteClick={target && target.costumes && target.costumes.length > 1 ?
                    this.handleDeleteCostume : null}
                onDrop={this.handleDrop}
                onDuplicateClick={this.handleDuplicateCostume}
                onExportClick={this.handleExportCostume}
                onItemClick={this.handleSelectCostume}
            >

 {this.state.showAIBackdropCreator && isStage ? (
                  <div
    style={{
        position: 'absolute',
        zIndex: 1000,
        top: '70px',
        left: '20px',
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        maxWidth: '850px',
        width: 'calc(100% - 80px)'
    }}
>
                        <BackdropCreator
                            onAddToProject={imageBase64 => {
                                this.handleAIBackdropAdd(imageBase64);
                                this.setState({
                                    showAIBackdropCreator: false
                                });
                            }}
                            onClose={() => {
                                this.setState({
                                    showAIBackdropCreator: false
                                });
                            }}
                        />
                    </div>
                ) : null}

{this.state.showAICostumeCreator && !isStage ? (
    <div
        style={{
            position: 'absolute',
            zIndex: 1000,
            top: '70px',
            left: '20px',
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            maxWidth: '850px',
            width: 'calc(100% - 80px)'
        }}
    >
        <SpriteCreator
    mode="costume"
    onAddToProject={imageBase64 => {
        this.handleAICostumeAdd(imageBase64);

        this.setState({
            showAICostumeCreator: false
        });
    }}
    onClose={() => {
        this.setState({
            showAICostumeCreator: false
        });
    }}
/>
    </div>
) : null}

                {target.costumes ?
                    <PaintEditorWrapper
                        selectedCostumeIndex={this.state.selectedCostumeIndex}
                    /> :
                    null
                }
            </AssetPanel>
        );
    }
}

CostumeTab.propTypes = {
    ariaLabel: PropTypes.string,
    ariaRole: PropTypes.string,
    dispatchUpdateRestore: PropTypes.func,
    editingTarget: PropTypes.string,
    intl: intlShape,
    isRtl: PropTypes.bool,
    onActivateSoundsTab: PropTypes.func.isRequired,
    onCloseImporting: PropTypes.func.isRequired,
    onNewLibraryBackdropClick: PropTypes.func.isRequired,
    onNewLibraryCostumeClick: PropTypes.func.isRequired,
    onShowImporting: PropTypes.func.isRequired,
    sprites: PropTypes.shape({
        id: PropTypes.shape({
            costumes: PropTypes.arrayOf(PropTypes.shape({
                url: PropTypes.string,
                name: PropTypes.string.isRequired,
                skinId: PropTypes.number
            }))
        })
    }),
    stage: PropTypes.shape({
        sounds: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string.isRequired
        }))
    }),
    vm: PropTypes.instanceOf(VM),
    dynamicCostumes: PropTypes.arrayOf(costumeShape),
    dynamicBackdrops: PropTypes.arrayOf(costumeShape)
};

const mapStateToProps = state => ({
    editingTarget: state.scratchGui.targets.editingTarget,
    isRtl: state.locales.isRtl,
    sprites: state.scratchGui.targets.sprites,
    stage: state.scratchGui.targets.stage,
    dragging: state.scratchGui.assetDrag.dragging,
    dynamicCostumes: state.scratchGui.dynamicAssets.costumes,
    dynamicBackdrops: state.scratchGui.dynamicAssets.backdrops
});

const mapDispatchToProps = dispatch => ({
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onNewLibraryBackdropClick: () => {
        dispatch(openBackdropLibrary());
    },
    onNewLibraryCostumeClick: () => {
        dispatch(openCostumeLibrary());
    },
    dispatchUpdateRestore: restoreState => {
        dispatch(setRestore(restoreState));
    },
    onCloseImporting: () => dispatch(closeAlertWithId('importingAsset')),
    onShowImporting: () => dispatch(showStandardAlert('importingAsset'))
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onNewLibraryCostumeClick: ownProps.onNewLibraryCostumeClick || dispatchProps.onNewLibraryCostumeClick,
    onNewLibraryBackdropClick: ownProps.onNewLibraryBackdropClick || dispatchProps.onNewLibraryBackdropClick
});

export default errorBoundaryHOC('Costume Tab')(
    injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(CostumeTab))
);
